import React, { useState } from 'react';
import { Modal, Button, Form, InputGroup, Row, Col, Tab, Tabs } from 'react-bootstrap';
import useModalHistory from '../../../hooks/useModalHistory';

const ViewCardModal = ({ show, onHide, cardData }) => {
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [showCvv, setShowCvv] = useState(false);
  const [isCardFlipped, setIsCardFlipped] = useState(false);

  useModalHistory(show, onHide, 'card-view-modal');
  
  if (!show) return null;
  if (!cardData) return null;
  
  // Create masked values with safe conversion
  const cardNumber = String(cardData.cardNumber || '');
  const maskedCardNumber = cardNumber 
    ? '•••• •••• •••• ' + cardNumber.slice(-4)
    : '•••• •••• •••• ••••';
    
  const maskedCvv = '•••';

  const handleCardFlip = () => {
    setIsCardFlipped(!isCardFlipped);
  };
  
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="common-modal"
    >
      <Modal.Header closeButton className="custom-header">
        <Modal.Title>View Bank Card</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Add a style tag to override the problematic CSS */}
        <style>
          {`
            /* Override the problematic CSS from forgot-password.css */
            .tab-content {
              display: block !important;
            }
          `}
        </style>
        
        <Tabs defaultActiveKey="view-details-tab" id="view-card-tabs">
          <Tab eventKey="view-details-tab" title="Card Information">
            <div className="tab-content-body">
              {/* Card Preview with Flip (for mobile only) */}
              <div className="card-flip-container d-md-none">
                <div 
                  className={`card-flip-inner ${isCardFlipped ? 'flipped' : ''}`}
                  onClick={handleCardFlip}
                >
                  {/* Front of card - using existing credit-card class */}
                  <div className="credit-card card-face card-front">
                    <div className="card-top">
                      <div className="card-brand">
                        <i className="far fa-credit-card"></i>
                      </div>
                      <div className="card-alias">
                        {cardData.alias || ''}
                      </div>
                    </div>
                    <div className="card-number">
                      {showCardNumber ? cardData.cardNumber : maskedCardNumber}
                    </div>
                    <div className="card-bottom">
                      <div className="card-holder">
                        {cardData.cardHolderName || ''}
                      </div>
                      <div className="card-exp">
                        {cardData.cardExp || ''}
                      </div>
                    </div>
                    <div className="flip-hint">
                      Tap to flip
                    </div>
                  </div>

                  {/* Back of card - using existing credit-card class with modifications */}
                  <div className="credit-card card-face card-back">
                    <div className="magnetic-strip"></div>
                    <div className="card-back-content">
                      <div className="cvv-section">
                        <div className="cvv-label">CVV/CVC</div>
                        <div className="cvv-number">
                          {showCvv ? cardData.cardCcv : maskedCvv}
                        </div>
                      </div>
                    </div>
                    <div className="flip-hint">
                      Tap to flip back
                    </div>
                  </div>
                </div>
              </div>

              {/* Toggle buttons for mobile CVV reveal */}
              <div className="d-md-none mb-3 text-center">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowCvv(!showCvv)}
                  className="me-2"
                >
                  <i className={`fas fa-${showCvv ? 'eye-slash' : 'eye'}`}></i>
                  {showCvv ? ' Hide' : ' Show'} CVV
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowCardNumber(!showCardNumber)}
                >
                  <i className={`fas fa-${showCardNumber ? 'eye-slash' : 'eye'}`}></i>
                  {showCardNumber ? ' Hide' : ' Show'} Number
                </Button>
              </div>

              <Form>
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">Card Alias:</Form.Label>
                  <div className="col-sm-9">
                    <Form.Control 
                      type="text" 
                      value={cardData.alias || ''} 
                      readOnly 
                    />
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">Cardholder Name:</Form.Label>
                  <div className="col-sm-9">
                    <Form.Control 
                      type="text" 
                      value={cardData.cardHolderName || ''} 
                      readOnly 
                    />
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">Account Number:</Form.Label>
                  <div className="col-sm-9">
                    <Form.Control 
                      type="text" 
                      value={cardData.accountNumber || ''} 
                      readOnly 
                    />
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">Card Number:</Form.Label>
                  <div className="col-sm-9">
                    <div className="masked-data">
                      <InputGroup>
                        <Form.Control
                          type="text"
                          value={showCardNumber ? cardData.cardNumber : maskedCardNumber}
                          readOnly
                          className="form-control"
                        />
                        <InputGroup.Text 
                          className="reveal-btn"
                          onClick={() => setShowCardNumber(!showCardNumber)}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className={`fas fa-${showCardNumber ? 'eye-slash' : 'eye'}`}></i>
                        </InputGroup.Text>
                      </InputGroup>
                    </div>
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">CVV/CVC:</Form.Label>
                  <div className="col-sm-9">
                    <div className="masked-data">
                      <InputGroup>
                        <Form.Control
                          type="text"
                          value={showCvv ? cardData.cardCcv : maskedCvv}
                          readOnly
                          className="form-control"
                        />
                        <InputGroup.Text 
                          className="reveal-btn"
                          onClick={() => setShowCvv(!showCvv)}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className={`fas fa-${showCvv ? 'eye-slash' : 'eye'}`}></i>
                        </InputGroup.Text>
                      </InputGroup>
                    </div>
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">Expiration Date:</Form.Label>
                  <div className="col-sm-9">
                    <Form.Control 
                      type="text" 
                      value={cardData.cardExp || ''} 
                      readOnly 
                    />
                  </div>
                </Form.Group>
              </Form>
            </div>
          </Tab>
          
          <Tab eventKey="view-billing-tab" title="Billing Address">
            <div className="tab-content-body">
              <Form>
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">Street:</Form.Label>
                  <div className="col-sm-9">
                    <Form.Control 
                      type="text" 
                      value={cardData.street || ''} 
                      readOnly 
                    />
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">City:</Form.Label>
                  <div className="col-sm-9">
                    <Form.Control 
                      type="text" 
                      value={cardData.city || ''} 
                      readOnly 
                    />
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">State/Province:</Form.Label>
                  <div className="col-sm-9">
                    <Form.Control 
                      type="text" 
                      value={cardData.state || ''} 
                      readOnly 
                    />
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">ZIP/Postal Code:</Form.Label>
                  <div className="col-sm-9">
                    <Form.Control 
                      type="text" 
                      value={cardData.zip || ''} 
                      readOnly 
                    />
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3 row">
                  <Form.Label className="col-sm-3 col-form-label">Country:</Form.Label>
                  <div className="col-sm-9">
                    <Form.Control 
                      type="text" 
                      value={cardData.country || ''} 
                      readOnly 
                    />
                  </div>
                </Form.Group>
              </Form>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};

export default ViewCardModal;